import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import MRTDataGridv2 from '../../../../../components/MRTDataGrid/MRTDataGridv2'
import { MRTColumnDef } from '../../../../../types/common'
import { useOrderFormContext } from '../../../context/OrderFormContext'
import BatchModal, { BatchData } from '../Modals/BatchModal'

interface BatchDetailsProps {
  prefix?: string
}

const BatchDetails: React.FC<BatchDetailsProps> = ({ prefix = '' }) => {
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false)
  const [selectedBatch, setSelectedBatch] = useState<BatchData | null>(null)
  const { watch, setValue, getValues } = useFormContext()
  const { variant } = useOrderFormContext()

  const currentPrefix = `${prefix}.batchDetails`

  const batches = watch(currentPrefix) || []

  useEffect(() => {
    if (!getValues(currentPrefix)) {
      setValue(currentPrefix, [])
    }
  }, [getValues, currentPrefix, setValue])

  const handleOpenBatchModal = (batch: BatchData | null = null) => {
    setSelectedBatch(batch)
    setIsBatchModalOpen(true)
  }

  const handleAddNewBatch = (batchData: BatchData) => {
    const currentBatches = getValues(currentPrefix) || []

    if (selectedBatch && selectedBatch.internalId) {
      // Editing: preserve internalId
      const updatedBatches = currentBatches.map((batch: BatchData) =>
        batch.internalId === selectedBatch.internalId ? { ...batchData, internalId: selectedBatch.internalId } : batch
      )
      setValue(currentPrefix, updatedBatches)
    } else {
      // Adding: assign unique internalId
      setValue(currentPrefix, [...currentBatches, { ...batchData, internalId: uuidv4() }])
    }
  }

  const handleDeleteBatch = (internalId: string) => {
    const currentBatches = getValues(currentPrefix) || []
    const updatedBatches = currentBatches.filter((batch: BatchData) => batch.internalId !== internalId)
    setValue(currentPrefix, updatedBatches)
  }

  const columns = useMemo<MRTColumnDef<BatchData>[]>(() => {
    return [
      {
        accessorKey: 'batchNumber',
        header: 'Batch Number',
        Cell: ({ cell }: any) => <Typography>{cell.getValue()}</Typography>,
      },
      {
        accessorKey: 'expiryDate',
        header: 'Expiry Date',
        Cell: ({ cell }: any) => (
          <Typography>
            {cell.getValue() ? dayjs(cell.getValue()).format('YYYY-MM-DD') : ''}
          </Typography>
        ),
      },
      {
        accessorKey: 'actions',
        header: '',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }: any) => {
          if (variant === 'summary') {
            return null
          }
          return (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Tooltip title='Edit'>
                <IconButton
                  color='primary'
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenBatchModal(row.original)
                  }}
                  size='small'
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title='Delete'>
                <IconButton
                  color='error'
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteBatch(row.original.internalId)
                  }}
                  size='small'
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )
        },
      },
    ]
  }, [variant])


  return (
    <Box sx={{ p: 2, width: '100%' }}>
      <MRTDataGridv2
        data={batches}
        columns={columns}
        componentPadding='0'
        componentMargin='-12px 0 4px 0'

        rightHeadingComponent={
          variant !== 'summary'
            ? (
              <Button
                startIcon={<AddCircleOutlinedIcon />}
                sx={{ textTransform: 'none' }}
                onClick={() => handleOpenBatchModal()}
              >
                Add Batch
              </Button>
            )
            : undefined
        }
        enablePagination={false}
        enableTopToolbar={false}
        enableBottomToolbar={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableSorting={true}
        initialState={{
          pagination: { pageSize: 5, pageIndex: 0 },
        }}
        state={{
          isLoading: false,
          showProgressBars: false,
        }}
        getRowId={(row) => row.internalId as string}
      />

      {isBatchModalOpen && (
        <BatchModal
          open={isBatchModalOpen}
          onClose={() => {
            setIsBatchModalOpen(false)
            setSelectedBatch(null)
          }}
          onSave={handleAddNewBatch}
          initialData={selectedBatch}
        />
      )}
    </Box>
  )
}

export default BatchDetails
